/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useEffect, useState, useMemo } from 'react';
import { toast } from 'react-toastify';
import styled from 'styled-components';

import Loading from '../../components/Loading';
import axios from '../../services/axios';

import { Container } from '../../styles/layout/Container';
import {
   PageHeader,
   PageHeaderText,
   PageSubtitle,
   PageTitle,
} from '../../styles/layout/PageHeader';
import {
   Section,
   SectionBody,
   SectionHeader,
   SectionTitle,
} from '../../styles/layout/Section';
import { FormGrid } from '../../styles/layout/FormGrid';
import { Field } from '../../styles/forms/Field';
import { ActionBar, ActionBarRight } from '../../styles/layout/ActionBar';
import { TableWrapper } from '../../styles/table/TableWrapper';
import { Table } from '../../styles/table/Table';
import { EmptyState } from '../../styles/feedback/EmptyState';
import PaginationBar from '../../components/PaginationBar/PaginationBar';
import SectionMeta, { SectionHeaderRow } from '../../components/SectionMeta';

// Estilos locais
const AlertBadge = styled.span`
   display: inline-flex;
   align-items: center;
   gap: 4px;
   padding: 4px 10px;
   border-radius: 4px;
   font-size: 11px;
   font-weight: 700;
   background: ${({ $severity }) => {
      if ($severity === 'high') return 'rgba(244, 67, 54, 0.12)';
      if ($severity === 'medium') return 'rgba(255, 152, 0, 0.12)';
      return 'rgba(255, 193, 7, 0.12)';
   }};
   color: ${({ $severity }) => {
      if ($severity === 'high') return '#c62828';
      if ($severity === 'medium') return '#e65100';
      return '#f57f17';
   }};

   &::before {
      content: '⚠️';
      font-size: 12px;
   }
`;

export default function LowStockProducts() {
   const [pdvs, setPdvs] = useState([]);
   const [isLoading, setIsLoading] = useState(false);

   const today = new Date()
      .toLocaleDateString('pt-BR', {
         year: 'numeric',
         month: '2-digit',
         day: '2-digit',
      })
      .split('/')
      .reverse()
      .join('-');

   // Filtros
   const [date, setDate] = useState(today);
   const [pdvId, setPdvId] = useState('');

   // Resultado
   const [lowStockData, setLowStockData] = useState(null);

   // Paginação
   const [currentPage, setCurrentPage] = useState(1);
   const itemsPerPage = 20;

   useEffect(() => {
      async function loadPdvs() {
         try {
            setIsLoading(true);
            const response = await axios.get('/pdvs');
            const raw = response?.data ?? [];
            const list = Array.isArray(raw) ? raw : raw?.pdvs ?? [];
            setPdvs(Array.isArray(list) ? list : []);
         } catch (err) {
            const msg =
               err?.response?.data?.errors?.[0] ?? 'Erro ao carregar PDVs.';
            toast.error(msg);
         } finally {
            setIsLoading(false);
         }
      }

      loadPdvs();
   }, []);

   const handleClearFilters = () => {
      const ok = window.confirm('Deseja limpar todos os filtros?');
      if (!ok) return;

      setDate(today);
      setPdvId('');
      setLowStockData(null);
      setCurrentPage(1);
   };

   const handleSearch = async (e) => {
      e.preventDefault();

      if (!date) {
         toast.error('Informe a data');
         return;
      }

      if (!pdvId) {
         toast.error('Selecione um PDV');
         return;
      }

      setIsLoading(true);

      try {
         const response = await axios.get(`/stock/low-stock/${pdvId}`, {
            params: { date },
         });

         setLowStockData(response.data);
         setCurrentPage(1);
      } catch (error) {
         const msg =
            error?.response?.data?.errors?.[0] ??
            'Erro ao buscar produtos em falta.';
         toast.error(msg);
         setLowStockData(null);
      } finally {
         setIsLoading(false);
      }
   };

   const currentProducts = useMemo(() => {
      if (!lowStockData?.products) return [];

      const start = (currentPage - 1) * itemsPerPage;
      const end = start + itemsPerPage;
      return lowStockData.products.slice(start, end);
   }, [lowStockData, currentPage]);

   const totalPages = lowStockData?.products
      ? Math.ceil(lowStockData.products.length / itemsPerPage)
      : 0;

   const getSeverity = (missingQtd) => {
      if (missingQtd >= 50) return 'high';
      if (missingQtd >= 20) return 'medium';
      return 'low';
   };

   const getSeverityLabel = (severity) => {
      if (severity === 'high') return 'Crítico';
      if (severity === 'medium') return 'Moderado';
      return 'Leve';
   };

   return (
      <Container>
         <Loading isLoading={isLoading} />

         <PageHeader>
            <PageHeaderText>
               <PageTitle>Produtos em Falta</PageTitle>
               <PageSubtitle>
                  Identifique produtos com estoque abaixo do mínimo
               </PageSubtitle>
            </PageHeaderText>
         </PageHeader>

         <Section>
            <SectionHeader>
               <SectionTitle>Filtros</SectionTitle>
            </SectionHeader>

            <SectionBody>
               <form onSubmit={handleSearch}>
                  <FormGrid>
                     <Field $col={4} $colMobile={12}>
                        <label>Data *</label>
                        <input
                           type="date"
                           value={date}
                           onChange={(e) => setDate(e.target.value)}
                           max={today}
                           required
                        />
                     </Field>

                     <Field $col={4} $colMobile={12}>
                        <label>PDV *</label>
                        <select
                           value={pdvId}
                           onChange={(e) => setPdvId(e.target.value)}
                           required
                        >
                           <option value="">Selecione...</option>
                           {pdvs.map((pdv) => (
                              <option key={String(pdv.id)} value={pdv.id}>
                                 {pdv.pdv_name ?? pdv.name ?? `${pdv.id}`}
                              </option>
                           ))}
                        </select>
                     </Field>

                     <Field $col={4} $colMobile={12} $alignEnd>
                        <label>&nbsp;</label>
                        <ActionBar>
                           <ActionBarRight>
                              <button
                                 type="button"
                                 onClick={handleClearFilters}
                                 disabled={isLoading}
                              >
                                 Limpar
                              </button>
                              <button type="submit" disabled={isLoading}>
                                 Consultar
                              </button>
                           </ActionBarRight>
                        </ActionBar>
                     </Field>
                  </FormGrid>
               </form>
            </SectionBody>
         </Section>

         {lowStockData && (
            <Section>
               <SectionHeader>
                  <SectionHeaderRow>
                     <SectionTitle>
                        Produtos em Falta
                        {lowStockData.total > 0 && (
                           <span style={{ color: '#f44336', marginLeft: '8px' }}>
                              ({lowStockData.total})
                           </span>
                        )}
                     </SectionTitle>

                     <SectionMeta
                        items={[
                           {
                              key: 'date',
                              label: 'Data:',
                              value: new Date(
                                 `${lowStockData.date  }T00:00:00`
                              ).toLocaleDateString('pt-BR'),
                           },
                           {
                              key: 'page',
                              label: 'Página',
                              value: `${currentPage}/${totalPages || 1}`,
                           },
                        ]}
                     />
                  </SectionHeaderRow>
               </SectionHeader>

               <SectionBody>
                  {currentProducts.length > 0 ? (
                     <>
                        <TableWrapper>
                           <Table>
                              <thead>
                                 <tr>
                                    <th>SKU</th>
                                    <th>Produto</th>
                                    <th>Categoria</th>
                                    <th style={{ textAlign: 'right' }}>
                                       Estoque Atual
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                       Estoque Mínimo
                                    </th>
                                    <th style={{ textAlign: 'right' }}>
                                       Faltando
                                    </th>
                                    <th style={{ textAlign: 'center' }}>
                                       Gravidade
                                    </th>
                                 </tr>
                              </thead>
                              <tbody>
                                 {currentProducts.map((p) => {
                                    const severity = getSeverity(p.missing_qtd);
                                    return (
                                       <tr key={String(p.prod_id)}>
                                          <td>{p.prod_sku}</td>
                                          <td>
                                             <strong>{p.prod_name}</strong>
                                          </td>
                                          <td>{p.category_name || '-'}</td>
                                          <td
                                             style={{
                                                textAlign: 'right',
                                                color: '#f44336',
                                                fontWeight: 600,
                                             }}
                                          >
                                             {p.stock_qtd}
                                          </td>
                                          <td style={{ textAlign: 'right' }}>
                                             {p.min_stock_qtd}
                                          </td>
                                          <td
                                             style={{
                                                textAlign: 'right',
                                                fontWeight: 700,
                                                color: '#c62828',
                                             }}
                                          >
                                             {p.missing_qtd}
                                          </td>
                                          <td style={{ textAlign: 'center' }}>
                                             <AlertBadge $severity={severity}>
                                                {getSeverityLabel(severity)}
                                             </AlertBadge>
                                          </td>
                                       </tr>
                                    );
                                 })}
                              </tbody>
                           </Table>
                        </TableWrapper>

                        <PaginationBar
                           currentPage={currentPage}
                           totalPages={totalPages}
                           onPageChange={setCurrentPage}
                           disabled={isLoading}
                        />
                     </>
                  ) : (
                     <EmptyState
                        title="✅ Nenhum produto em falta!"
                        description="Todos os produtos estão acima do estoque mínimo nesta data."
                     />
                  )}
               </SectionBody>
            </Section>
         )}

         {!lowStockData && !isLoading && (
            <EmptyState
               title="Nenhum resultado"
               description="Preencha os filtros e clique em Consultar."
            />
         )}
      </Container>
   );
}
